import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  ArrowLeft, Globe, RotateCcw, Info, Play, Pause,
  Thermometer, CloudRain, Wind, Factory, TreePine, Car
} from 'lucide-react';

/**
 * CLIMATE SIMULATOR - Environmental Science Simulation
 * Students change CO2 levels, watch glaciers melt, sea levels rise
 */
const ClimateSimulator = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [language, setLanguage] = useState('en');
  const [showInfo, setShowInfo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [year, setYear] = useState(2024);
  const [co2Level, setCo2Level] = useState(420);
  const [deforestation, setDeforestation] = useState(20);
  const [industrialization, setIndustrialization] = useState(50);
  const [animationFrame, setAnimationFrame] = useState(0);

  // Calculated values
  const [temperature, setTemperature] = useState(15);
  const [seaLevel, setSeaLevel] = useState(0);
  const [glacierSize, setGlacierSize] = useState(100);
  const [extremeWeather, setExtremeWeather] = useState(10);

  // Translations
  const t = {
    title: language === 'en' ? 'Climate Simulator' : 'जलवायु सिम्युलेटर',
    subtitle: language === 'en' ? 'Watch how human activities affect our planet' : 'देखें मानव गतिविधियां हमारे ग्रह को कैसे प्रभावित करती हैं',
    year: language === 'en' ? 'Year' : 'वर्ष',
    co2: language === 'en' ? 'CO₂ Level (ppm)' : 'CO₂ स्तर (ppm)',
    deforestation: language === 'en' ? 'Deforestation' : 'वनों की कटाई',
    industrialization: language === 'en' ? 'Industrialization' : 'औद्योगीकरण',
    temperature: language === 'en' ? 'Global Temperature' : 'वैश्विक तापमान',
    seaLevel: language === 'en' ? 'Sea Level Rise' : 'समुद्र स्तर वृद्धि',
    glaciers: language === 'en' ? 'Glacier Coverage' : 'हिमनद आवरण',
    extremeWeather: language === 'en' ? 'Extreme Weather Events' : 'चरम मौसम घटनाएं',
    preindustrial: language === 'en' ? 'Pre-industrial: 280 ppm' : 'पूर्व-औद्योगिक: 280 ppm',
    current: language === 'en' ? 'Current: ~420 ppm' : 'वर्तमान: ~420 ppm',
    dangerous: language === 'en' ? 'Dangerous: >450 ppm' : 'खतरनाक: >450 ppm',
    scenarios: language === 'en' ? 'Scenarios' : 'परिदृश्य',
    bestCase: language === 'en' ? 'Best Case (Net Zero)' : 'सर्वोत्तम (नेट जीरो)',
    currentTrend: language === 'en' ? 'Current Trend' : 'वर्तमान प्रवृत्ति',
    worstCase: language === 'en' ? 'Worst Case' : 'सबसे खराब',
    reset: language === 'en' ? 'Reset to 2024' : '2024 पर रीसेट',
    info: language === 'en' ? 'How it works' : 'यह कैसे काम करता है',
    infoText: language === 'en' 
      ? 'Adjust CO₂ levels, deforestation, and industrialization to see their effects on global temperature, sea levels, and glaciers. Watch decades pass and see how our choices today shape the future!'
      : 'वैश्विक तापमान, समुद्र स्तर और हिमनदों पर प्रभाव देखने के लिए CO₂ स्तर, वनों की कटाई और औद्योगीकरण को समायोजित करें। दशकों को बीतते देखें और देखें कि आज के हमारे विकल्प भविष्य को कैसे आकार देते हैं!',
    arctic: language === 'en' ? 'Arctic' : 'आर्कटिक',
    ocean: language === 'en' ? 'Ocean' : 'महासागर',
    land: language === 'en' ? 'Land' : 'भूमि',
  };

  // Calculate effects based on inputs
  useEffect(() => {
    // Base temperature calculation (simplified climate model)
    const co2Effect = (co2Level - 280) * 0.005; // ~3°C per doubling of CO2
    const forestEffect = deforestation * 0.01;
    const industryEffect = industrialization * 0.005;
    
    const newTemp = 14 + co2Effect + forestEffect + industryEffect;
    setTemperature(Math.round(newTemp * 10) / 10);
    
    // Sea level rise (mm per year accelerating with temperature)
    const tempAnomaly = newTemp - 14;
    setSeaLevel(Math.round(tempAnomaly * tempAnomaly * 20));
    
    // Glacier coverage
    const glacierLoss = Math.max(0, 100 - tempAnomaly * 15 - (year - 2024) * 0.5);
    setGlacierSize(Math.round(glacierLoss));
    
    // Extreme weather probability
    setExtremeWeather(Math.min(100, 10 + tempAnomaly * 10 + (co2Level - 280) / 10));
  }, [co2Level, deforestation, industrialization, year]);

  // Animation loop
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setAnimationFrame(f => f + 1);
    }, 50);
    return () => clearInterval(interval);
  }, [isPlaying]);

  // Draw climate visualization
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Sky gradient (color changes with temperature)
    const tempFactor = Math.min(1, (temperature - 14) / 4);
    const skyGradient = ctx.createLinearGradient(0, 0, 0, height * 0.6);
    skyGradient.addColorStop(0, `rgb(${100 + tempFactor * 50}, ${150 - tempFactor * 50}, ${220 - tempFactor * 50})`);
    skyGradient.addColorStop(1, `rgb(${180 + tempFactor * 50}, ${200 - tempFactor * 30}, ${230 - tempFactor * 30})`);
    ctx.fillStyle = skyGradient;
    ctx.fillRect(0, 0, width, height * 0.6);
    
    // Sun (larger/more intense with temperature)
    const sunSize = 30 + tempFactor * 20;
    ctx.fillStyle = `rgba(255, ${200 - tempFactor * 50}, 0, ${0.8 + tempFactor * 0.2})`;
    ctx.beginPath();
    ctx.arc(width - 80, 60, sunSize, 0, Math.PI * 2);
    ctx.fill();
    
    // Clouds (fewer with temperature/deforestation)
    const cloudOpacity = Math.max(0.1, 0.8 - tempFactor * 0.3);
    ctx.fillStyle = `rgba(255, 255, 255, ${cloudOpacity})`;
    for (let i = 0; i < 5 - tempFactor * 2; i++) {
      const cx = (i * 150 + animationFrame * 0.5) % (width + 100) - 50;
      const cy = 50 + i * 30;
      ctx.beginPath();
      ctx.arc(cx, cy, 25, 0, Math.PI * 2);
      ctx.arc(cx + 25, cy - 10, 20, 0, Math.PI * 2);
      ctx.arc(cx + 50, cy, 22, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Ocean (rises with sea level)
    const seaY = height * 0.6 + seaLevel * 0.3;
    const oceanGradient = ctx.createLinearGradient(0, seaY, 0, height);
    oceanGradient.addColorStop(0, '#0ea5e9');
    oceanGradient.addColorStop(1, '#0369a1');
    ctx.fillStyle = oceanGradient;
    ctx.fillRect(0, seaY, width, height - seaY);
    
    // Waves
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 2;
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(0, seaY + 10 + i * 15);
      for (let x = 0; x < width; x += 20) {
        ctx.lineTo(x, seaY + 10 + i * 15 + Math.sin((x + animationFrame * 2) / 30) * 5);
      }
      ctx.stroke();
    }
    
    // Land mass
    ctx.fillStyle = '#8b5a2b';
    ctx.beginPath();
    ctx.moveTo(0, seaY);
    ctx.lineTo(0, height * 0.5);
    ctx.lineTo(width * 0.3, height * 0.45);
    ctx.lineTo(width * 0.4, seaY);
    ctx.closePath();
    ctx.fill();
    
    // Green land (decreases with deforestation)
    const greenCoverage = (100 - deforestation) / 100;
    ctx.fillStyle = '#22c55e';
    ctx.beginPath();
    ctx.moveTo(0, height * 0.5);
    ctx.lineTo(0, height * 0.5 - 20 * greenCoverage);
    ctx.lineTo(width * 0.25, height * 0.48 - 15 * greenCoverage);
    ctx.lineTo(width * 0.3, height * 0.47);
    ctx.lineTo(width * 0.3, height * 0.45);
    ctx.closePath();
    ctx.fill();
    
    // Trees
    ctx.fillStyle = '#15803d';
    const numTrees = Math.floor(15 * greenCoverage);
    for (let i = 0; i < numTrees; i++) {
      const tx = 20 + i * 18;
      const ty = height * 0.48 - (i % 3) * 10;
      ctx.beginPath();
      ctx.moveTo(tx, ty);
      ctx.lineTo(tx - 8, ty + 20);
      ctx.lineTo(tx + 8, ty + 20);
      ctx.closePath();
      ctx.fill();
    }
    
    // Glacier/Ice (shrinks with temperature)
    const glacierWidth = width * 0.25 * (glacierSize / 100);
    const glacierX = width * 0.65;
    
    // Glacier gradient
    const iceGradient = ctx.createLinearGradient(glacierX, height * 0.3, glacierX + glacierWidth, height * 0.5);
    iceGradient.addColorStop(0, '#e0f2fe');
    iceGradient.addColorStop(0.5, '#bae6fd');
    iceGradient.addColorStop(1, '#7dd3fc');
    ctx.fillStyle = iceGradient;
    
    ctx.beginPath();
    ctx.moveTo(glacierX, seaY);
    ctx.lineTo(glacierX, height * 0.35);
    ctx.lineTo(glacierX + glacierWidth * 0.3, height * 0.3);
    ctx.lineTo(glacierX + glacierWidth * 0.7, height * 0.32);
    ctx.lineTo(glacierX + glacierWidth, height * 0.4);
    ctx.lineTo(glacierX + glacierWidth, seaY);
    ctx.closePath();
    ctx.fill();
    
    // Ice texture
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(glacierX + i * glacierWidth / 5, height * 0.35 + i * 3);
      ctx.lineTo(glacierX + (i + 1) * glacierWidth / 5, seaY);
      ctx.stroke();
    }
    
    // Melting water drops (more if hot)
    if (temperature > 15) {
      ctx.fillStyle = 'rgba(14, 165, 233, 0.6)';
      const dropCount = Math.floor((temperature - 15) * 3);
      for (let i = 0; i < dropCount; i++) {
        const dx = glacierX + Math.random() * glacierWidth;
        const dy = height * 0.4 + ((animationFrame * 2 + i * 30) % 60);
        if (dy < seaY) {
          ctx.beginPath();
          ctx.arc(dx, dy, 3, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    
    // Factories (based on industrialization)
    const numFactories = Math.ceil(industrialization / 25);
    for (let i = 0; i < numFactories; i++) {
      const fx = 50 + i * 60;
      const fy = height * 0.52;
      
      // Building
      ctx.fillStyle = '#374151';
      ctx.fillRect(fx, fy - 30, 30, 30);
      
      // Chimney
      ctx.fillRect(fx + 10, fy - 50, 8, 20);
      
      // Smoke
      ctx.fillStyle = `rgba(100, 100, 100, ${0.3 + industrialization / 200})`;
      for (let s = 0; s < 4; s++) {
        const sx = fx + 14 + Math.sin(animationFrame / 10 + s) * 10;
        const sy = fy - 50 - s * 20 - (animationFrame % 30);
        const sr = 8 + s * 5;
        ctx.beginPath();
        ctx.arc(sx, sy, sr, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // CO2 indicator
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(10, 10, 150, 60);
    ctx.fillStyle = '#fbbf24';
    ctx.font = 'bold 14px sans-serif';
    ctx.fillText(`CO₂: ${co2Level} ppm`, 20, 35);
    ctx.fillStyle = temperature > 16 ? '#ef4444' : '#22c55e';
    ctx.fillText(`${temperature.toFixed(1)}°C`, 20, 55);
    
    // Year display
    ctx.fillStyle = 'white';
    ctx.font = 'bold 24px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(year.toString(), width - 20, 40);
    
  }, [animationFrame, temperature, seaLevel, glacierSize, co2Level, deforestation, industrialization, year]);

  // Scenario presets
  const loadScenario = (scenario) => {
    switch(scenario) {
      case 'best':
        setCo2Level(350);
        setDeforestation(5);
        setIndustrialization(30);
        setYear(2050);
        break;
      case 'current':
        setCo2Level(420);
        setDeforestation(20);
        setIndustrialization(50);
        setYear(2024);
        break;
      case 'worst':
        setCo2Level(550);
        setDeforestation(60);
        setIndustrialization(80);
        setYear(2100);
        break;
    }
  };

  const reset = () => {
    setCo2Level(420);
    setDeforestation(20);
    setIndustrialization(50);
    setYear(2024);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-green-900/20 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-slate-900/90 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/science')} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-400" />
            </button>
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <Thermometer className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{t.title}</h1>
              <p className="text-xs text-slate-400">{t.subtitle}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button onClick={() => setIsPlaying(!isPlaying)} className={`p-2 rounded-lg transition-colors ${isPlaying ? 'bg-green-600' : 'bg-slate-800'}`}>
              {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white" />}
            </button>
            <button onClick={() => setShowInfo(!showInfo)} className={`p-2 rounded-lg transition-colors ${showInfo ? 'bg-green-600' : 'bg-slate-800 hover:bg-slate-700'}`}>
              <Info className="w-5 h-5 text-white" />
            </button>
            <button onClick={() => setLanguage(language === 'en' ? 'hi' : 'en')}
              className="flex items-center gap-1 px-3 py-2 bg-slate-800 rounded-full hover:bg-slate-700 transition-colors">
              <Globe className="w-4 h-4 text-green-400" />
              <span className="text-xs text-slate-300">{language === 'en' ? 'हिन्दी' : 'ENG'}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        {showInfo && (
          <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-xl">
            <p className="text-sm text-green-200">{t.infoText}</p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Controls */}
          <div className="space-y-4">
            {/* Year Slider */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-300">{t.year}</span>
                  <span className="text-2xl font-bold text-white">{year}</span>
                </div>
                <input type="range" min="1900" max="2100" value={year} onChange={(e) => setYear(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500" />
              </CardContent>
            </Card>

            {/* Environmental Controls */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 space-y-4">
                {/* CO2 */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Factory className="w-4 h-4 text-gray-400" /> {t.co2}
                    </span>
                    <span className={`text-lg font-bold ${co2Level > 450 ? 'text-red-400' : co2Level > 350 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {co2Level}
                    </span>
                  </div>
                  <input type="range" min="280" max="600" value={co2Level} onChange={(e) => setCo2Level(parseInt(e.target.value))}
                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-gray-500" />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>280</span>
                    <span>420</span>
                    <span>600</span>
                  </div>
                </div>

                {/* Deforestation */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <TreePine className="w-4 h-4 text-green-400" /> {t.deforestation}
                    </span>
                    <span className="text-lg font-bold text-orange-400">{deforestation}%</span>
                  </div>
                  <input type="range" min="0" max="80" value={deforestation} onChange={(e) => setDeforestation(parseInt(e.target.value))}
                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-orange-500" />
                </div>

                {/* Industrialization */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Car className="w-4 h-4 text-blue-400" /> {t.industrialization}
                    </span>
                    <span className="text-lg font-bold text-blue-400">{industrialization}%</span>
                  </div>
                  <input type="range" min="0" max="100" value={industrialization} onChange={(e) => setIndustrialization(parseInt(e.target.value))}
                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500" />
                </div>
              </CardContent>
            </Card>

            {/* Scenarios */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-slate-300 mb-3">{t.scenarios}</h3>
                <div className="space-y-2">
                  <Button onClick={() => loadScenario('best')} variant="outline" className="w-full justify-start border-green-600 text-green-400 hover:bg-green-600/20">
                    <TreePine className="w-4 h-4 mr-2" /> {t.bestCase}
                  </Button>
                  <Button onClick={() => loadScenario('current')} variant="outline" className="w-full justify-start border-yellow-600 text-yellow-400 hover:bg-yellow-600/20">
                    <Factory className="w-4 h-4 mr-2" /> {t.currentTrend}
                  </Button>
                  <Button onClick={() => loadScenario('worst')} variant="outline" className="w-full justify-start border-red-600 text-red-400 hover:bg-red-600/20">
                    <Thermometer className="w-4 h-4 mr-2" /> {t.worstCase}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Button onClick={reset} variant="outline" className="w-full border-slate-600 text-slate-300">
              <RotateCcw className="w-4 h-4 mr-2" /> {t.reset}
            </Button>
          </div>

          {/* Visualization */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <canvas ref={canvasRef} width={700} height={400} className="w-full rounded-xl" />
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <Thermometer className={`w-6 h-6 mx-auto mb-2 ${temperature > 16 ? 'text-red-400' : 'text-blue-400'}`} />
                  <div className="text-xs text-slate-400">{t.temperature}</div>
                  <div className={`text-2xl font-bold ${temperature > 16 ? 'text-red-400' : temperature > 15 ? 'text-yellow-400' : 'text-green-400'}`}>
                    {temperature.toFixed(1)}°C
                  </div>
                  <div className="text-xs text-slate-500">+{(temperature - 14).toFixed(1)}°C</div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <Wind className="w-6 h-6 mx-auto mb-2 text-cyan-400" />
                  <div className="text-xs text-slate-400">{t.seaLevel}</div>
                  <div className="text-2xl font-bold text-cyan-400">+{seaLevel}mm</div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <CloudRain className={`w-6 h-6 mx-auto mb-2 ${glacierSize < 50 ? 'text-red-400' : 'text-blue-400'}`} />
                  <div className="text-xs text-slate-400">{t.glaciers}</div>
                  <div className={`text-2xl font-bold ${glacierSize < 50 ? 'text-red-400' : 'text-blue-400'}`}>{glacierSize}%</div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <Wind className={`w-6 h-6 mx-auto mb-2 ${extremeWeather > 30 ? 'text-orange-400' : 'text-green-400'}`} />
                  <div className="text-xs text-slate-400">{t.extremeWeather}</div>
                  <div className={`text-2xl font-bold ${extremeWeather > 30 ? 'text-orange-400' : 'text-green-400'}`}>{Math.round(extremeWeather)}%</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClimateSimulator;
